import { useIntl, IntlShape } from "react-intl";
import { DateTime } from "luxon";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import VitalsIcon from "../../components/VitalsIcon";
import {
  isTemperatureAlertable,
  isBloodPressureAlertable,
  isHeartRateAlertable,
  isRespiratoryRateAlertable,
  isBloodOxygenAlertable,
} from "../../domain/vitalsDomain";
import { type PatientQuery, type Vitals } from "../../graphql/generated";
import { useServiceBag } from "../../services/ServiceBag";

const DefaultVitals = {
  eventId: "Default-Vitals-00",
  eventType: "Default_Hospital_Admission",
  patientId: "Patient-00",
  patientIdType: "Default",
  bloodOxygen: 98,
  diastolicBloodPressure: 80,
  heartRate: 72,
  height: 175,
  respiratoryRate: 16,
  systolicBloodPressure: 120,
  temperature: 98.0,
  weight: 70,
  backPainPoints: null,
  frontPainPoints: null,
};

interface VitalsBarProps {
  vitals: Vitals | undefined | null;
  patient: PatientQuery | undefined;
}
function VitalsBar({ vitals, patient }: VitalsBarProps) {
  const intl = useIntl();

  return (
    <Box marginX="2em">
      <Stack
        direction="row"
        spacing="auto"
        alignItems="center"
        sx={{ mx: "1em", marginTop: "1em" }}
      >
        <PatientPanel patient={patient} />{" "}
        {vitalsInfo(vitals, intl).map((vitalsInfo) => (
          <VisitInfoPanel {...vitalsInfo} key={vitalsInfo.title} />
        ))}
      </Stack>
    </Box>
  );
}

function vitalsInfo(
  vitals: Vitals | undefined | null,
  intl: IntlShape
): VisitInfoPanelParams[] {
  if (!vitals) {
    return [];
  }
  const bloodO2Text = `${vitals.bloodOxygen || ""}%`;
  const bloodPressureText = `${vitals.systolicBloodPressure || ""}/${
    vitals.diastolicBloodPressure || ""
  }`;

  return [
    //These signify the addition of height and weight to vitals.
    {
      title: intl.formatMessage({ id: "demographics.height" }),
      value: vitals.height || "",
      unit: intl.formatMessage({ id: "demographics.height-unit" }),
    },
    {
      title: intl.formatMessage({ id: "demographics.weight" }),
      value: vitals.weight || "",
      unit: intl.formatMessage({ id: "kg" }),
    },
    {
      title: intl.formatMessage({ id: "demographics.temperature" }),
      value: vitals.temperature || "",
      unit: intl.formatMessage({ id: "demographics.temperature-unit" }),
      alert: isTemperatureAlertable(vitals.temperature),
    },
    {
      title: intl.formatMessage({ id: "demographics.blood-pressure" }),
      value: bloodPressureText,
      unit: intl.formatMessage({ id: "demographics.blood-pressure-unit" }),
      alert: isBloodPressureAlertable(
        vitals.systolicBloodPressure,
        vitals.diastolicBloodPressure
      ),
    },
    {
      title: intl.formatMessage({ id: "demographics.heartRate" }),
      value: vitals.heartRate || "",
      unit: intl.formatMessage({ id: "demographics.heartRate-unit" }),
      alert: isHeartRateAlertable(vitals.heartRate),
    },
    {
      title: intl.formatMessage({ id: "demographics.respiratoryRate" }),
      value: vitals.respiratoryRate || "",
      unit: intl.formatMessage({ id: "/Min" }),
      alert: isRespiratoryRateAlertable(vitals.respiratoryRate),
    },
    {
      title: intl.formatMessage({ id: "demographics.bloodOxygen" }),
      value: bloodO2Text,
      unit: intl.formatMessage({ id: "demographics.bloodOxygen-unit" }),
      alert: isBloodOxygenAlertable(vitals.bloodOxygen),
    },
  ];
}

interface VisitInfoPanelParams {
  title: string;
  value: string | number;
  unit: string;
  showIcon?: boolean;
  alert?: boolean;
}
function VisitInfoPanel({
  title,
  value,
  unit,
  showIcon = true,
  alert = false,
}: VisitInfoPanelParams) {
  const variant = alert ? "outlined" : "elevation";
  const borderProps = alert
    ? { borderBlockColor: "red", borderInlineColor: "red" }
    : {};
  return (
    <Card
      variant={variant}
      sx={{
        width: "auto",
        paddingInline: "1em",
        borderRadius: "60px",
        ...borderProps,
      }}
    >
      <Stack alignItems="flex-start" mx="6px">
        {/* commenting this out as there are no titles in the new Design
        <Typography variant="subtitle2" mx="auto" noWrap={true}>
          {title}
        </Typography> */}
        <Stack
          direction="row"
          alignContent="flex-end"
          alignItems="baseline"
          spacing="5px"
          mx="auto"
        >
          {showIcon && <VitalsIcon />}
          <Typography variant="datum">{value}</Typography>
          <Typography variant="caption">{unit}</Typography>
        </Stack>
      </Stack>
    </Card>
  );
}

interface ageData {
  years: number;
  months: number;
}

export function calculateAge(birthDate: DateTime, currDate: DateTime): ageData {
  const ageDiff = currDate.diff(birthDate, ["years", "months", "days"]);
  // if anyone of the age componets are undefined, they'll be assigned zero
  let { years = 0, months = 0 } = ageDiff.toObject();
  // incase incorrect age is added by the patient/doctor
  if (years < 0 || months < 0) {
    years = 0;
    months = 0;
  }
  return { years, months };
}

export function toFormattedAge(
  { years, months }: ageData,
  intl: IntlShape
): string {
  // in case of incorrect age
  if (years === 0 && months === 0) {
    return "undefined";
  }
  const formattedAge = intl.formatMessage(
    { id: "ageFormat" },
    { years, months }
  );
  return formattedAge;
}

interface PatientPanelProps {
  patient: PatientQuery | undefined;
}

function PatientPanel({ patient }: PatientPanelProps) {
  const { envService } = useServiceBag();
  const intl = useIntl();

  // patient info variables
  const patientInfo = patient?.patient;
  const fullname =
    String(patientInfo?.firstName) + " " + String(patientInfo?.lastName);
  const displayName = patientInfo?.firstName ? fullname : "";
  const birthday = patientInfo?.birthday;
  const birthdayLuxon = DateTime.fromISO(birthday);
  const currentTime = envService.currentTime();
  const { years, months } = calculateAge(birthdayLuxon, currentTime);
  const formattedAge = toFormattedAge({ years, months }, intl);
  console.log("fullname is: ", fullname);

  // age etc. will go here as well.
  return (
    <Stack direction="row" spacing="6px">
      <Typography fontWeight="bold" fontSize="2em">
        {displayName}{" "}
      </Typography>
      {birthday && (
        <Typography
          fontWeight="bold"
          //Adding additional styles for the age
          sx={{ paddingLeft: "2em", alignSelf: "center" }}
        >
          {formattedAge}
        </Typography>
      )}
    </Stack>
  );
}

export { DefaultVitals };
export default VitalsBar;
